import { LogLevel } from './LogLevel'
import { type IProfiler, Profiler } from './Profiler'
import type { LogRecord } from './types'

/**
 * 命名空间日志实例，由 LoggerManager 创建，不直接 new。
 */
export interface ILogger {
  /**
   * 输出 ERROR 级日志。
   *
   * @param msg - 已拼接的消息字符串
   */
  error(msg: string): void
  /**
   * 输出 WARN 级日志。
   *
   * @param msg - 已拼接的消息字符串
   */
  warn(msg: string): void
  /**
   * 输出 INFO 级日志。
   *
   * @param msg - 已拼接的消息字符串
   */
  info(msg: string): void
  /**
   * 输出 DEBUG 级日志。
   *
   * @param msg - 已拼接的消息字符串
   */
  debug(msg: string): void
  /**
   * 派生子命名空间，如 gsplat → gsplat:loader。
   *
   * @param sub - 子命名空间片段
   * @returns 子命名空间 Logger
   */
  child(sub: string): ILogger
  /**
   * 当前命名空间是否会输出该级别，用于跳过昂贵的字符串拼接。
   *
   * @param level - 待判断的级别
   * @returns 会输出返回 true
   */
  isEnabled(level: LogLevel): boolean
  /** 性能埋点子接口 */
  readonly perf: IProfiler
}

/**
 * LoggerManager 为 Logger 提供的内部能力，Logger 通过它完成级别判定、输出与子实例创建。
 */
export interface LoggerHost {
  /** 时钟，返回毫秒 */
  now(): number
  /** perf 是否启用 */
  isPerfEnabled(): boolean
  /**
   * 判断指定命名空间是否输出该级别。
   *
   * @param namespace - 命名空间
   * @param level - 级别
   */
  isEnabled(namespace: string, level: LogLevel): boolean
  /**
   * 经级别过滤后输出一条记录。
   *
   * @param record - 日志记录
   */
  emit(record: LogRecord): void
  /**
   * 不经级别过滤直接写入 sink（perf 输出使用）。
   *
   * @param record - 日志记录
   */
  writeRecord(record: LogRecord): void
  /**
   * 获取或创建指定命名空间的 Logger。
   *
   * @param namespace - 命名空间
   */
  getLogger(namespace: string): ILogger
}

/**
 * 命名空间 Logger 实现。所有输出经 LoggerHost 路由，便于统一级别控制与 sink 派发。
 */
export class Logger implements ILogger {
  private readonly _namespace: string
  private readonly _host: LoggerHost
  readonly perf: IProfiler

  /**
   * @param namespace - 命名空间
   * @param host - LoggerManager 提供的宿主能力
   */
  constructor(namespace: string, host: LoggerHost) {
    this._namespace = namespace
    this._host = host
    this.perf = new Profiler({
      namespace,
      isEnabled: () => host.isPerfEnabled(),
      now: () => host.now(),
      emit: (record) => host.writeRecord(record),
    })
  }

  error(msg: string): void {
    this._log(LogLevel.ERROR, msg)
  }

  warn(msg: string): void {
    this._log(LogLevel.WARN, msg)
  }

  info(msg: string): void {
    this._log(LogLevel.INFO, msg)
  }

  debug(msg: string): void {
    this._log(LogLevel.DEBUG, msg)
  }

  child(sub: string): ILogger {
    return this._host.getLogger(this._namespace === '' ? sub : `${this._namespace}:${sub}`)
  }

  isEnabled(level: LogLevel): boolean {
    return this._host.isEnabled(this._namespace, level)
  }

  /** 按级别过滤后构造记录并输出 */
  private _log(level: LogLevel, msg: string): void {
    if (!this._host.isEnabled(this._namespace, level)) {
      return
    }
    this._host.emit({
      level,
      namespace: this._namespace,
      message: msg,
      timestamp: this._host.now(),
    })
  }
}
